import { useState, useEffect, useRef, useCallback } from "react";
import { WS_URL } from "../api/chat.js";

export interface Message {
  id: string;
  role: "user" | "assistant";
  text: string;
  pending?: boolean;
}

type ChatStatus = "connecting" | "ready" | "error" | "limit_reached" | "disconnected";

interface UseChatOptions {
  sessionId: string;
  botId: string;
  initialMessages?: Message[];
  onCapReached?: (message: string) => void;
}

export function useChat({ sessionId, botId, initialMessages, onCapReached }: UseChatOptions) {
  const [messages, setMessages]   = useState<Message[]>(initialMessages ?? []);
  const [status, setStatus]       = useState<ChatStatus>("connecting");
  const [capMessage, setCapMessage] = useState<string | null>(null);
  const wsRef    = useRef<WebSocket | null>(null);
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retries  = useRef(0);
  const pendingId = useRef<string | null>(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const url = `${WS_URL}?session_id=${encodeURIComponent(sessionId)}&bot_id=${encodeURIComponent(botId)}`;
    const ws  = new WebSocket(url);
    wsRef.current = ws;
    setStatus("connecting");

    ws.onopen = () => {
      setStatus("ready");
      retries.current = 0;
    };

    ws.onmessage = (ev) => {
      let msg: { type: string; text?: string; error?: string; cta?: string };
      try { msg = JSON.parse(ev.data); } catch { return; }

      if (msg.type === "chunk" && msg.text) {
        if (pendingId.current === null) {
          // Server started a new turn unprompted (e.g. workflow collect prompt after RAG response)
          const newMsg: Message = { id: crypto.randomUUID(), role: "assistant", text: msg.text!, pending: true };
          pendingId.current = newMsg.id;
          setMessages(prev => [...prev, newMsg]);
        } else {
          setMessages(prev => prev.map(m =>
            m.id === pendingId.current
              ? { ...m, text: m.text + msg.text!, pending: true }
              : m
          ));
        }
      } else if (msg.type === "done") {
        setMessages(prev => prev.map(m =>
          m.id === pendingId.current ? { ...m, pending: false } : m
        ));
        pendingId.current = null;
      } else if (msg.type === "error") {
        const isLimit = msg.error?.toLowerCase().includes("limit") || msg.error?.toLowerCase().includes("cap");
        if (isLimit) {
          setStatus("limit_reached");
          setCapMessage(msg.cta ?? msg.error ?? "Message limit reached.");
          onCapReached?.(msg.cta ?? msg.error ?? "Message limit reached.");
        }
        setMessages(prev => prev.map(m =>
          m.id === pendingId.current ? { ...m, text: msg.error ?? "An error occurred.", pending: false } : m
        ));
        pendingId.current = null;
      }
    };

    ws.onerror = () => setStatus("error");

    ws.onclose = (ev) => {
      if (ev.code === 1000) { setStatus("disconnected"); return; }
      // Exponential back-off reconnect (max 30s)
      const delay = Math.min(1000 * 2 ** retries.current, 30_000);
      retries.current += 1;
      setStatus("connecting");
      retryRef.current = setTimeout(connect, delay);
    };
  }, [sessionId, botId, onCapReached]);

  useEffect(() => {
    connect();
    return () => {
      if (retryRef.current) clearTimeout(retryRef.current);
      wsRef.current?.close(1000);
    };
  }, [connect]);

  const sendMessage = useCallback((text: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (status === "limit_reached") return;

    const userMsg: Message = { id: crypto.randomUUID(), role: "user",      text };
    const asstMsg: Message = { id: crypto.randomUUID(), role: "assistant", text: "", pending: true };
    pendingId.current = asstMsg.id;
    setMessages(prev => [...prev, userMsg, asstMsg]);
    wsRef.current.send(JSON.stringify({ action: "$default", text }));
  }, [status]);

  /**
   * Fire a suggested-trigger chip. Echoes the chip label as the user's turn so
   * the conversation reads naturally, then tells the server to bypass the
   * intent classifier and route straight to this trigger.
   */
  const sendSuggestedTrigger = useCallback((triggerId: string, label: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (status === "limit_reached") return;

    const userMsg: Message = { id: crypto.randomUUID(), role: "user",      text: label };
    const asstMsg: Message = { id: crypto.randomUUID(), role: "assistant", text: "", pending: true };
    pendingId.current = asstMsg.id;
    setMessages(prev => [...prev, userMsg, asstMsg]);
    wsRef.current.send(JSON.stringify({
      action: "$default",
      suggested_trigger_id: triggerId,
      label,
    }));
  }, [status]);

  return { messages, status, capMessage, sendMessage, sendSuggestedTrigger };
}
